import { INTENT } from '@brighte-enterprise/common/constants';

import { PartnerConfigStepFlow, StepVisibilityClause, StepVisibilityCondition } from '@typeDefs/groups';

/**
 * The nurture branch (INT-1387) — partner-neutral.
 *
 * A researching buyer is offered the questions they still have (`topics`) and the answers behind
 * them (`education`) before any vendor step. Having read them they either carry on to vendors or
 * say they are not ready, in which case the vendor steps drop away and the journey ends on
 * `contact`. A buying buyer never enters the branch and keeps the plain vendor flow.
 *
 * The whole tail is built by `nurtureVendorSteps()` rather than listed per channel, because the
 * four steps' conditions reference each other: `education` narrows `topics`, and
 * `connect-with-vendors` negates part of `education`. Splitting them across channel configs is how
 * they drift apart.
 */

// The topic values with content behind them in the marketplace. `other` is free text, so a buyer
// who picks only it has asked nothing there is an answer to — `education` is skipped, and the
// journey ends on `contact`.
export const NURTURE_EDUCATION_TOPICS = ['pricing', 'sizes-brands', 'process'];
export const NURTURE_TOPICS_NOTE_OPTION = 'other';

// Values only — the labels and the answer behind each topic live in the marketplace, so a copy
// change there is a marketplace release rather than a config change.
export const NURTURE_TOPICS_STEP_CONFIG = {
  options: [...NURTURE_EDUCATION_TOPICS, NURTURE_TOPICS_NOTE_OPTION],
  noteOption: NURTURE_TOPICS_NOTE_OPTION,
};

const RESEARCHING: StepVisibilityClause = {
  questionId: 'intent',
  operator: 'equals',
  values: [INTENT.interest],
};

/**
 * "The buyer's category selection is not exactly this one category."
 *
 * Use it to keep the nurture steps away from a single-category journey the education content has
 * nothing to say to — an EV-charger-only buyer, say, when the content is about solar and battery
 * sizing, pricing and install.
 *
 * `questionId` is whichever category question the channel actually asks: `categories`, or
 * `bundle-categories` for a channel that has no `categories` step. On `bundle-categories` the
 * stored answer is the RESOLVED bundle slug, not the ticked subcategories — the marketplace maps a
 * full selection onto a bundle (solar + battery + charger -> `solar-battery-ev-charger`) and falls
 * back to the first slug when no bundle matches. No bundle is a single category, so a solo pick
 * always falls back, which is what makes the bare category slug the value to match.
 *
 * `notEquals` is exact-set negation in the evaluator, so this is true for every selection that
 * merely CONTAINS the category, and true when the question is unanswered.
 */
export const notOnlyCategory = (questionId: string, category: string): StepVisibilityClause => ({
  questionId,
  operator: 'notEquals',
  values: [category],
});

export type NurtureVendorStepsOptions = {
  /**
   * Extra clauses ANDed onto the two nurture steps, for a channel that wants the branch narrower
   * than "the buyer is researching" — see `notOnlyCategory`. They gate `topics` and `education`
   * only; the vendor steps stay reachable, so a buyer excluded here keeps the plain vendor flow.
   */
  alsoShowWhen?: StepVisibilityClause[];
  /**
   * Whether the channel has a `connect-with-vendors` connector before `recommended-vendors`.
   * Channels whose earlier steps already lead into vendors (commbank) pass `false`.
   */
  connector?: boolean;
};

/**
 * The nurture steps and the vendor steps that follow them, in order. Place after the channel's
 * `intent` step and before `contact`.
 *
 * `contact` is deliberately NOT returned: it stays unconditional on every channel. Both ways out of
 * the branch — "not ready", and the free-text-only topic — end there, and the marketplace switches
 * that step's copy on the education answer rather than routing to a step of its own.
 *
 * Every clause reads a step that sits ABOVE the step carrying it, which the evaluator requires: it
 * reads a not-yet-shown step's answer as unanswered rather than failing, so a reorder would break
 * the branching silently. Any `alsoShowWhen` clause must therefore name a step the channel asks
 * before this tail.
 */
export function nurtureVendorSteps(options: NurtureVendorStepsOptions = {}): PartnerConfigStepFlow[] {
  const { alsoShowWhen = [], connector = true } = options;

  // Every clause is built or cloned at the point of use, so nothing in the returned tree is shared:
  // not between the two nurture steps that carry the same clauses, not between two calls, and not
  // with the module constants above or with the caller's own `alsoShowWhen` array. This config is
  // only ever read and serialised, so the practical hazard is small — but a test that mutates one
  // channel's clause must not be able to reach another channel's.
  const cloneClause = (clause: StepVisibilityClause): StepVisibilityClause => ({
    ...clause,
    values: [...clause.values],
  });
  const entersBranch = () => [RESEARCHING, ...alsoShowWhen].map(cloneClause);

  // The vendor steps drop away for a researching buyer who opts out, and for one whose only topic
  // was the free-text option. `notEquals` is true when unanswered, so a buyer who never enters the
  // branch is unaffected and keeps today's flow exactly.
  const stillHeadingToVendors = (): StepVisibilityCondition => ({
    match: 'all',
    clauses: [
      { questionId: 'education', operator: 'notEquals', values: ['not-ready'] },
      { questionId: 'topics', operator: 'notEquals', values: [NURTURE_TOPICS_NOTE_OPTION] },
    ],
  });

  // The topics with content behind them, as `education` tests them and as the connector negates
  // them. One helper for both so the pair cannot drift apart.
  const educationTopics = (operator: 'in' | 'notIn'): StepVisibilityClause => ({
    questionId: 'topics',
    operator,
    values: [...NURTURE_EDUCATION_TOPICS],
  });

  // `education` ends on the vendor CTA, so a buyer who read it goes straight to
  // `recommended-vendors` — the connector would be a second one asking the same question. `notIn`
  // is the negation of the `education` clause, and it is true when `topics` is unanswered, so the
  // connector keeps its place for a buyer who never entered the branch.
  const connectorStep: PartnerConfigStepFlow = {
    id: 'connect-with-vendors',
    required: true,
    visibleWhen: {
      match: 'all',
      clauses: [...stillHeadingToVendors().clauses, educationTopics('notIn')],
    },
  };

  return [
    { id: 'topics', required: true, visibleWhen: { match: 'all', clauses: entersBranch() } },
    // Only shown when a picked topic has content behind it — `in`, so picking several qualifies.
    // It repeats `entersBranch` rather than leaning on `topics` being hidden (and so unanswered):
    // each step states its own visibility, the way the commbank pair does.
    {
      id: 'education',
      required: true,
      visibleWhen: { match: 'all', clauses: [...entersBranch(), educationTopics('in')] },
    },
    ...(connector ? [connectorStep] : []),
    { id: 'recommended-vendors', required: true, visibleWhen: stillHeadingToVendors() },
  ];
}
